import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import pluralize from 'pluralize';
import { useTranslation } from 'react-i18next';
import { Button, Card, Input, Space, Tooltip, Typography } from 'antd';
import { CloseOutlined, SearchOutlined } from '@ant-design/icons';
import { pickTagColor } from './utils/pickTagColor';
import { createTranslationKey } from './utils/createTranslationKey';
import { CONDITION_BUTTONS, OPERATORS } from './utils/insertTemplateKey';
import { ARRAY_TYPES, TAG_TYPES } from './utils/tagTypes';
import { decorateTagTree, filterDecoratedTree } from './utils/filterTagTree';

const { Text } = Typography;

// Keeps the caret and selection inside the editor while clicking the panel,
// the same trick Jodit applies to its own toolbar buttons.
const keepEditorFocus = (event) => event.preventDefault();

// Every clickable chip of the panel shares one box, styled in
// admin/template-editor.scss. The card's own close button is left out on
// purpose: it is not a chip.
const CHIP_CLASS = 'template-editor__keys-panel__chip';

// One nesting step. A branch renders its children inside its own padded box,
// so the paddings already add up: every level but the root carries this step
// once, and never `depth` times over.
const NESTING_PX = 12;

const tagShape = PropTypes.shape({
  key: PropTypes.string.isRequired,
  type: PropTypes.string,
  subcontent: PropTypes.arrayOf(PropTypes.shape({}))
});

/**
 * Panel listing the template keys, docked next to the editor. Clicking a key
 * inserts it at the caret without closing the panel. Everything but the key
 * list is pinned, so the shortcuts stay one click away however far the author
 * has scrolled down the list.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {Array} props.tags - The tag tree fetched for the picked use case.
 * @param {Function} props.onInsertKey - Called with (value, type, selectedLabel).
 * @param {Function} props.onInsertOperator - Called with (symbol, label).
 * @param {Function} props.onSaveSelection - Bookmarks the caret before the
 * search box steals the focus.
 * @param {Function} props.onClose - Called when the author closes the panel.
 * @returns {JSX.Element} The panel.
 */
export const TemplateKeysPanel = ({
  tags,
  onInsertKey,
  onInsertOperator,
  onSaveSelection,
  onClose
}) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');

  // createTranslationKey is not free: the labels are computed once per tree,
  // the keystrokes only walk pre-normalized strings.
  const decoratedTags = useMemo(
    () => decorateTagTree(tags, t, pluralize),
    [tags, t]
  );
  const visibleTags = useMemo(
    () => filterDecoratedTree(decoratedTags, query),
    [decoratedTags, query]
  );

  const renderTree = (items, parentKeyPath = '', parentLabel = '', depth = 0) =>
    items.map((item) => {
      let currentKey = parentKeyPath
        ? `${parentKeyPath}.${item.key}`
        : item.key;
      const color = pickTagColor(item.type);

      // Labels are derived from the path without its array markers.
      const optionLabel = createTranslationKey('tag', currentKey, t, pluralize);
      const selectedLabel = parentLabel
        ? `${parentLabel}.${optionLabel}`
        : optionLabel;

      if (ARRAY_TYPES.includes(item.type)) {
        currentKey += '.index';
        if (item.type === 'array-array-object') currentKey += '.index';
      }

      if (item.subcontent && item.subcontent.length > 0) {
        return (
          <div key={currentKey} style={{ paddingLeft: depth ? NESTING_PX : 0 }}>
            <Text
              strong
              className="template-editor__keys-panel__group"
              style={{ color }}
            >
              {optionLabel}
            </Text>
            {renderTree(item.subcontent, currentKey, optionLabel, depth + 1)}
          </div>
        );
      }

      return (
        <div key={currentKey} style={{ paddingLeft: depth ? NESTING_PX : 0 }}>
          <Button
            type="link"
            size="small"
            className={CHIP_CLASS}
            style={{ color }}
            onMouseDown={keepEditorFocus}
            onClick={() =>
              onInsertKey(`{{${currentKey}}}`, item.type, selectedLabel)
            }
          >
            {optionLabel}
          </Button>
        </div>
      );
    });

  const section = (title, children) => (
    <div>
      <Text type="secondary">{title}</Text>
      <div>{children}</div>
    </div>
  );

  return (
    <Card
      size="small"
      className="template-editor__keys-panel"
      title={t('template-documents.keys_panel.title')}
      extra={
        <Button
          type="text"
          size="small"
          icon={<CloseOutlined />}
          aria-label={t('buttons.close')}
          onClick={onClose}
        />
      }
    >
      <div className="template-editor__keys-panel__static">
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <Space size={[8, 4]} wrap>
            <Text type="secondary">{t('template-documents.types.title')}</Text>
            {TAG_TYPES.map((type) => (
              <Text
                key={type}
                style={{ color: pickTagColor(type), fontSize: 12 }}
              >
                {t(`template-documents.types.${type}`)}
              </Text>
            ))}
          </Space>
          {section(
            t('template-documents.keys_panel.shortcuts'),
            <Space size={[8, 8]} wrap>
              <Button
                size="small"
                className={CHIP_CLASS}
                style={{ color: pickTagColor('Date') }}
                onMouseDown={keepEditorFocus}
                onClick={() => onInsertKey('{{current.day}}', 'Date')}
              >
                {t('template-documents.keys_panel.today')}
              </Button>
              <Button
                size="small"
                className={CHIP_CLASS}
                style={{ color: pickTagColor('String') }}
                onMouseDown={keepEditorFocus}
                onClick={() => onInsertKey('{{generated_by}}', 'String')}
              >
                {t('template-documents.keys_panel.sender')}
              </Button>
            </Space>
          )}
          {section(
            t('template-documents.keys_panel.conditions'),
            <Space size={[8, 8]} wrap>
              {CONDITION_BUTTONS.map(({ id, value, displayText }) => (
                <Button
                  key={id}
                  size="small"
                  className={CHIP_CLASS}
                  style={{ color: pickTagColor('Boolean') }}
                  onMouseDown={keepEditorFocus}
                  onClick={() => onInsertKey(value, 'String', displayText)}
                >
                  {t(`template-documents.keys_panel.${id}`)}
                </Button>
              ))}
            </Space>
          )}
          {section(
            t('template-documents.keys_panel.operators'),
            <Space size={[8, 8]} wrap>
              {/* Shown as symbols to keep the pinned block short; the wording
                  stays the accessible name and the inserted text. */}
              {OPERATORS.map(({ label, symbol }) => (
                <Tooltip key={symbol} title={label}>
                  <Button
                    size="small"
                    className={CHIP_CLASS}
                    aria-label={label}
                    onMouseDown={keepEditorFocus}
                    onClick={() => onInsertOperator(symbol, label)}
                  >
                    {symbol}
                  </Button>
                </Tooltip>
              ))}
            </Space>
          )}
          {/* The input has to take the focus, so the caret is bookmarked
              instead of being held in place. The wrapper catches the mouse
              down wherever it lands, the clear button included. */}
          <div role="presentation" onMouseDown={onSaveSelection}>
            <Input
              size="small"
              allowClear
              prefix={<SearchOutlined />}
              placeholder={t('template-documents.keys_panel.search')}
              aria-label={t('template-documents.keys_panel.search')}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
          </div>
          <Text type="secondary">
            {t('template-documents.keys_panel.keys')}
          </Text>
        </Space>
      </div>
      <div className="template-editor__keys-panel__scroll">
        {visibleTags.length ? (
          renderTree(visibleTags)
        ) : (
          <Text type="secondary">
            {t('template-documents.keys_panel.no_result')}
          </Text>
        )}
      </div>
    </Card>
  );
};

TemplateKeysPanel.propTypes = {
  tags: PropTypes.arrayOf(tagShape),
  onInsertKey: PropTypes.func.isRequired,
  onInsertOperator: PropTypes.func.isRequired,
  onSaveSelection: PropTypes.func,
  onClose: PropTypes.func.isRequired
};

TemplateKeysPanel.defaultProps = {
  tags: [],
  onSaveSelection: () => {}
};
